import { composeProgress } from "apollo/utils";
import {
  AUCTION_STATUS_NOT_STARTED,
  SEARCH_ACTION_SEND_REQUEST,
  SEARCH_ACTION_START_MANUAL_SEARCH,
  TRACK_EVENTS,
} from "core/consts";
import { useDateLocale } from "core/hooks/useDateLocale";
import { getSearchActions } from "core/model/auctions";
import { getErrorMessage } from "core/model/utils/errors";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { Auction, Careprovider, SolutionFilterReasons } from "core/types";
import { useFaxPatient } from "dsl/atoms/FaxPatient/useFaxPatient";
import {
  useStartAutomaticSearch,
  useStartManualSearch,
} from "dsl/atoms/PatientActions";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { getFilterReasons } from "dsl/ecosystems/PatientSearchMerge/Components/SearchPageTables/Tables/shared";
import useSearchAction from "dsl/hooks/useSearchAction";
import { useTracking } from "react-tracking";
import { useLocale, useTranslations } from "translations";
import { ConfirmFaxGDPRModal } from "./ConfirmFaxGDPR";
import { ConfirmSendModal } from "./ConfirmSend";
import { ConfirmSendFaxModal } from "./ConfirmSendFax";

export const CONFIRM_SEND = "confirm-send";
export const CONFIRM_FAX_GDPR = "confirm-fax-gdpr";
export const CONFIRM_SEND_FAX = "confirm-send-fax";

export type ContactAnywayModals =
  | typeof CONFIRM_SEND
  | typeof CONFIRM_FAX_GDPR
  | typeof CONFIRM_SEND_FAX;

const useHandleConfirmSend = ({
  auction,
  careprovider,
  recommendationId,
  refresh,
  sendFax,
  setModalOpen,
}: {
  auction: Auction;
  careprovider: Careprovider;
  recommendationId: number;
  refresh: () => void;
  sendFax?: boolean;
  setModalOpen: React.Dispatch<
    React.SetStateAction<ContactAnywayModals | null>
  >;
}) => {
  const { trackEvent } = useTracking();
  const translations = useTranslations();
  const toast = useToast();
  const { [SEARCH_ACTION_START_MANUAL_SEARCH]: canStartManualSearch } =
    getSearchActions(auction);

  const [startManualSearch, startManualSearchProgress] = useStartManualSearch({
    auction,
  });
  const [startAutomaticSearch, startAutomaticSearchProgress] =
    useStartAutomaticSearch({
      auction,
    });
  const [sendRequests, sendRequestsProgress] = useSearchAction({
    actionType: SEARCH_ACTION_SEND_REQUEST,
  });

  const { faxAuctionRequest } = useFaxPatient();

  async function handleConfirm() {
    try {
      trackEvent({
        careprovider_id: careprovider.id,
        name: TRACK_EVENTS.CONTACT_ANYWAY_MODAL_CONFIRM_BUTTON_CLICKED,
      });

      //   start search if not started yet
      if (auction.status === AUCTION_STATUS_NOT_STARTED) {
        if (canStartManualSearch) {
          await startManualSearch();
        } else {
          await startAutomaticSearch();
        }
      }

      //   send requests
      await sendRequests({
        auction,
        context: { requests: [{ id: recommendationId }] },
        onCompleted: async (auctionWithRequests) => {
          setModalOpen(null);

          toast({
            color: "success",
            message:
              translations.search.addProvider.successfullySentSingleRequest,
          });

          if (!sendFax || !auctionWithRequests?.requests?.length) {
            return;
          }

          //   send faxes if request is for a faxable provider
          try {
            const request = auctionWithRequests.requests?.find(
              (request) => request?.careprovider?.id === careprovider.id,
            );

            if (!request) {
              return;
            }

            await faxAuctionRequest(auction, request);
          } catch (err) {
            console.error(
              `failed to send fax from filtered provider in search merge page - ${getErrorMessage(
                err,
              )}`,
            );
          }
        },
      });
    } catch (err) {
      console.error(`error contacting anyway: ${getErrorMessage(err)}`);
    } finally {
      refresh();
    }
  }

  return {
    handleConfirm,
    loading: composeProgress([
      sendRequestsProgress,
      startManualSearchProgress,
      startAutomaticSearchProgress,
    ]),
  };
};

export function ContactAnywayConfirmationModal({
  auction,
  careprovider,
  modalOpen,
  recommendationId,
  refresh,
  sendFax,
  setModalOpen,
  solutionFilterReasons,
}: {
  auction: Auction;
  careprovider: Careprovider;
  modalOpen: ContactAnywayModals | null;
  recommendationId: number;
  refresh: () => void;
  sendFax?: boolean;
  setModalOpen: React.Dispatch<
    React.SetStateAction<ContactAnywayModals | null>
  >;
  solutionFilterReasons: SolutionFilterReasons;
}) {
  const translations = useTranslations();
  const getOntology = useGetOntology();
  const localeString = useLocale();
  const { currentLocale: locale } = useDateLocale();
  const { handleConfirm, loading } = useHandleConfirmSend({
    auction,
    careprovider,
    recommendationId,
    refresh,
    sendFax,
    setModalOpen,
  });

  const onClose = () => setModalOpen(null);
  const filterReasons = getFilterReasons({
    careprovider,
    getOntology,
    locale,
    localeString,
    solutionFilterReasons,
    translations,
  });

  switch (modalOpen) {
    case CONFIRM_SEND: {
      return (
        <ConfirmSendModal
          filterReasons={filterReasons.value}
          handleConfirm={handleConfirm}
          open
          queryProgress={loading}
          onClose={onClose}
        />
      );
    }
    case CONFIRM_FAX_GDPR: {
      return (
        <ConfirmFaxGDPRModal
          filterReasons={filterReasons.value}
          handleConfirm={() => setModalOpen(CONFIRM_SEND_FAX)}
          open
          onClose={onClose}
        />
      );
    }
    case CONFIRM_SEND_FAX: {
      return (
        <ConfirmSendFaxModal
          filterReasons={filterReasons.value}
          handleConfirm={handleConfirm}
          open
          providerName={careprovider.name ?? ""}
          queryProgress={loading}
          onClose={onClose}
        />
      );
    }
    default: {
      return null;
    }
  }
}
